import { Temporal } from '@js-temporal/polyfill'
import { useIntervalFn } from '@vueuse/core'
import { computed, ref } from 'vue'
import { globalUtils, useFormatting } from '@gat-ship/utils'
import { openHours } from '../model/support-constants'

function pad(value: number) {
  return String(value).padStart(2, '0')
}

/**
 * Live "is Gatship support open right now" state plus the display
 * helpers around it (opening-hours labels, time-zone context).
 * Re-evaluated every minute. Shared by SupportView and the
 * user-settings Support panel.
 */
export function useSupportOpenHours() {
  const { formatTime } = useFormatting()

  const nowUpdatedEveryMinute = ref(Temporal.Now.zonedDateTimeISO())

  useIntervalFn(() => {
    nowUpdatedEveryMinute.value = Temporal.Now.zonedDateTimeISO(
      globalUtils.date.getCurrentTimeZone()
    )
  }, 60 * 1000)

  function getOpenHoursFrom() {
    return Temporal.Now.zonedDateTimeISO(globalUtils.date.getCurrentTimeZone())
      .with({
        hour: openHours.from.hour,
        minute: openHours.from.minute
      })
      .toInstant()
      .toString()
  }

  function getOpenHoursTo() {
    return Temporal.Now.zonedDateTimeISO(globalUtils.date.getCurrentTimeZone())
      .with({
        hour: openHours.to.hour,
        minute: openHours.to.minute
      })
      .toInstant()
      .toString()
  }

  const isSupportOpen = computed(() => {
    const now = nowUpdatedEveryMinute.value
    // Support is staffed Mon-Fri (Saturday = 6, Sunday = 7).
    if (now.dayOfWeek === 6 || now.dayOfWeek === 7) {
      return false
    }
    const from = Temporal.Now.zonedDateTimeISO(openHours.from.timeZone).with({
      hour: openHours.from.hour,
      minute: openHours.from.minute
    })
    const isBeforeOpenHours = Temporal.ZonedDateTime.compare(from, now) >= 0

    const to = Temporal.Now.zonedDateTimeISO(openHours.to.timeZone).with({
      hour: openHours.to.hour,
      minute: openHours.to.minute
    })
    const isAfterOpenHours = Temporal.ZonedDateTime.compare(to, now) <= 0
    return !(isAfterOpenHours || isBeforeOpenHours)
  })

  // Open hours rendered in the user's own time zone. We may need to
  // account for the days for other timezones, but for now this is good.
  const openHoursLabel = computed(
    () =>
      `Mon - Fri (${formatTime(getOpenHoursFrom())} - ${formatTime(getOpenHoursTo())})`
  )

  /** Gatship's office hours in Gatship's own time zone, e.g. "08:00 - 16:00 Europe/Oslo". */
  const gatshipLocalTimeLabel = `${pad(openHours.from.hour)}:${pad(openHours.from.minute)} - ${pad(openHours.to.hour)}:${pad(openHours.to.minute)} ${openHours.from.timeZone}`

  const userTimeZone = Temporal.Now.timeZoneId()
  const isGatshipTimeZone = userTimeZone === openHours.from.timeZone

  return {
    isSupportOpen,
    openHoursLabel,
    gatshipLocalTimeLabel,
    userTimeZone,
    isGatshipTimeZone
  }
}
